/*
Copyright (C) 2026  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import { observer } from "mobx-react";
import { onSwiftRefresh } from "@src/components/modules/TemplateModule/TopBar/refresh";
import * as React from "react";
import styled from "styled-components";
import { saveAs } from "file-saver";

import MainTemplate from "@src/components/modules/TemplateModule/MainTemplate";
import Navigation from "@src/components/modules/NavigationModule/Navigation";
import PageHeader from "@src/components/smart/PageHeader";
import { ThemePalette } from "@src/components/Theme";
import EmptyState from "@src/components/ui/Lists/EmptyState";
import ListToolbar, {
  ClearFiltersButton,
  FilterSelect,
  RefreshButton,
  Segmented,
  ToolbarCount,
  ToolbarDateRange,
  ToolbarSearch,
  ToolbarSpacer,
  matchesSearch,
  useListQuery,
} from "@src/components/ui/Lists/ListToolbar";
import type { ListQuery } from "@src/components/ui/Lists/ListToolbar";
import {
  Cell,
  ListHeader,
  ListRow,
  Muted,
  Pill,
  Primary,
  Secondary,
  Stack,
  T9,
  formatDateTime,
  humanise,
  pluralise,
} from "@src/components/ui/Lists/ListTable";
import type { ListColumn } from "@src/components/ui/Lists/ListTable";
import {
  ChevronDown,
  CircleCheck,
  CircleX,
  Clock,
  Download,
  History,
  LoaderCircle,
  SearchX,
} from "lucide-react";
import auditSource from "@src/sources/AuditSource";
import notificationStore from "@src/stores/NotificationStore";

import type { AuditFilters, AuditRecord } from "@src/sources/AuditSource";

const KINDS = [
  { label: "Migration runs", value: "execution" },
  { label: "Changes (who did what)", value: "api" },
  { label: "Operations & checks", value: "operation" },
];
// Operations done on the platforms outside the API (coriolis_common.audit_ops).
const OPERATIONS: { [action: string]: string } = {
  "verify-migration": "Check after migration",
  "verify-status": "Status check",
  "write-test-data": "Test data written on source",
  "create-source-vm": "Source VM created",
  "create-or-update-source-vm": "Source VM created/updated",
  "power-on-source": "Source VM started",
  "power-off-source": "Source VM stopped",
  "delete-source-vm": "Source VM deleted",
  "delete-vm": "VM deleted",
  "delete-image": "Image deleted",
  "allow-remote-access": "Remote access allowed",
  "change-vm-ip": "VM IP changed",
};
const opLabel = (action: string | null) =>
  OPERATIONS[action || ""] || (action || "").replace(/^dr-/, "DR: ").replace(/-/g, " ");
const RESULTS: { [group: string]: string[] } = {
  succeeded: ["COMPLETED", "succeeded", "verified"],
  failed: ["ERROR", "failed", "mismatch", "unreachable"],
  cancelled: ["CANCELED", "FORCE_CANCELED"],
};
const FILTER_KEYS = ["q", "kind", "result", "actor", "action", "rtype", "from", "to"];

export const AUDIT_COLUMNS: ListColumn[] = [
  { key: "time", label: "Time", width: "150px" },
  { key: "actor", label: "Actor", width: "120px" },
  { key: "what", label: "Action", width: "minmax(220px, 1.8fr)" },
  { key: "type", label: "Resource type", width: "130px" },
  { key: "details", label: "Target / details", width: "minmax(180px, 1.4fr)" },
  { key: "result", label: "Result", width: "120px" },
];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  background: var(--t9-bg-surface);
  border: 1px solid ${T9.slate200};
  border-radius: 8px;
  overflow: hidden;
`;
const Scroll = styled.div`
  flex-grow: 1;
  min-height: 0;
  overflow: auto;
`;
const Inner = styled.div`
  min-width: 980px;
`;
const Footnote = styled.div`
  padding: 10px 16px;
  border-top: 1px solid ${T9.slate200};
  font-size: 12px;
  color: ${T9.slate500};
`;
const Details = styled.div`
  padding: 12px 16px 14px 16px;
  background: ${T9.slate50};
  border-bottom: 1px solid ${T9.slate200};
  font-size: 13px;
  color: ${T9.slate700};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const MonoBlock = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  color: ${T9.slate700};
`;
const Chevron = styled(ChevronDown)<{ open?: boolean }>`
  color: ${T9.slate400};
  transform: rotate(${props => (props.open ? 180 : 0)}deg);
  transition: transform 150ms;
  margin-left: 7px;
`;
const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${T9.slate300};
  background: var(--t9-bg-surface);
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${T9.slate700};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${T9.slate50};
    color: ${T9.slate900};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 48px;
  color: ${T9.slate500};
  font-size: 14px;
  .spin {
    animation: audit-spin 1s linear infinite;
  }
  @keyframes audit-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const when = (iso: string | null) => formatDateTime(iso);

const duration = (seconds?: number | null) => {
  if (seconds === undefined || seconds === null) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min ${seconds % 60} s`;
};

const describe = (r: AuditRecord) => {
  if (r.kind === "execution") {
    const d = r.details || {};
    const route = d.source && d.destination
      ? `${d.source.name || d.source.type || "?"} → ${d.destination.name || d.destination.type || "?"}`
      : "";
    return { what: `${r.action === "deployment" ? "Deployment" : "Sync"} of ${r.name || "?"}`, extra: route };
  }
  if (r.kind === "operation") {
    const d = r.details || {};
    return {
      what: `${opLabel(r.action)}: ${r.name || r.resource_id || "?"}`,
      extra: `${d.backfilled ? "(backfilled) " : ""}${d.performed_by || ""}`,
    };
  }
  return {
    what: `${r.action || r.method} ${r.resource_type || ""}`.trim(),
    extra: r.name || r.resource_id || "",
  };
};

const actorOf = (r: AuditRecord) => r.user_name || (r.user_id ? r.user_id.slice(0, 8) : "");

const ResultPill = ({ status }: { status: string | null }) => {
  if (!status) return <Pill tone="neutral" icon={Clock}>Unknown</Pill>;
  if (RESULTS.succeeded.includes(status)) {
    return <Pill tone="success" icon={CircleCheck}>{status === "COMPLETED" ? "Completed" : "Succeeded"}</Pill>;
  }
  if (RESULTS.failed.includes(status)) {
    return <Pill tone="danger" icon={CircleX}>{status === "ERROR" ? "Error" : "Failed"}</Pill>;
  }
  if (RESULTS.cancelled.includes(status)) return <Pill tone="warning" icon={CircleX}>Cancelled</Pill>;
  return <Pill tone="info" icon={LoaderCircle} spin>{humanise(status)}</Pill>;
};

const csvCell = (v: any) => {
  const s = v === null || v === undefined ? "" : typeof v === "string" ? v : JSON.stringify(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const toCsv = (records: AuditRecord[]) => {
  const head = ["time_utc", "kind", "action", "target_type", "target_id", "name", "user", "project",
    "result", "http_status", "source", "destination", "started_at", "finished_at", "duration_seconds",
    "summary", "errors", "request_id", "path", "performed_by", "reason", "evidence"];
  const rows = records.map(r => {
    const d = r.details || {};
    return [r.created_at, r.kind, r.action, r.resource_type, r.resource_id, r.name, r.user_name || r.user_id,
      r.project_id, r.status, r.status_code, d.source && (d.source.name || d.source.id),
      d.destination && (d.destination.name || d.destination.id), d.started_at, d.finished_at,
      d.duration_seconds, (d.summary || []).join(" | "), d.errors, r.request_id, r.path, d.performed_by, d.reason,
      d.evidence].map(csvCell).join(",");
  });
  return [head.join(","), ...rows].join("\n");
};

type Props = {
  query: ListQuery;
};

type State = {
  records: AuditRecord[];
  loading: boolean;
  expanded: number | null;
};

@observer
class AuditPageContent extends React.Component<Props, State> {
  state: State = { records: [], loading: false, expanded: null };

  offSwiftRefresh?: () => void;

  componentWillUnmount() {
    this.offSwiftRefresh?.();
  }

  componentDidMount() {
    // The top bar's Refresh reloads this page's data.
    this.offSwiftRefresh = onSwiftRefresh(() => {
      this.load();
    });
    document.title = "Audit history · SwiftIQ";
    this.load();
  }

  componentDidUpdate(prev: Props) {
    const keys = ["kind", "from", "to"];
    if (keys.some(k => prev.query.get(k) !== this.props.query.get(k))) {
      this.load();
    }
  }

  async load() {
    this.setState({ loading: true });
    try {
      const q = this.props.query;
      const f: AuditFilters = { kind: q.get("kind"), limit: 1000 };
      if (q.get("from")) f.since = new Date(`${q.get("from")}T00:00:00`).toISOString();
      if (q.get("to")) f.until = new Date(`${q.get("to")}T23:59:59`).toISOString();
      this.setState({ records: await auditSource.list(f) });
    } catch (err: any) {
      notificationStore.alert(`Could not load the audit history: ${err?.data?.description || err?.message || err}`, "error");
    } finally {
      this.setState({ loading: false });
    }
  }

  get filtersActive() {
    return FILTER_KEYS.some(k => this.props.query.get(k));
  }

  clearFilters() {
    const changes: { [k: string]: null } = {};
    FILTER_KEYS.forEach(k => {
      changes[k] = null;
    });
    this.props.query.set(changes);
  }

  filtered(except?: string): AuditRecord[] {
    const q = this.props.query;
    const text = q.get("q");
    const result = q.get("result");
    const actor = q.get("actor");
    const action = q.get("action");
    const rtype = q.get("rtype");
    return this.state.records.filter(r => {
      if (except !== "result" && result && !(RESULTS[result] || []).includes(r.status || "")) return false;
      if (actor && actorOf(r) !== actor) return false;
      if (action && (r.action || r.method) !== action) return false;
      if (rtype && r.resource_type !== rtype) return false;
      if (text) {
        const d = r.details || {};
        return matchesSearch(text, [
          r.name,
          r.user_name,
          r.user_id,
          r.resource_id,
          r.request_id,
          r.path,
          d.source?.name,
          d.destination?.name,
          ...(d.instances || []),
        ]);
      }
      return true;
    });
  }

  exportCsv(records: AuditRecord[]) {
    const blob = new Blob([toCsv(records)], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `swift-audit-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  renderDetails(r: AuditRecord) {
    const d = r.details || {};
    if (r.kind === "execution") {
      return (
        <Details>
          <div>
            <b>{r.action === "deployment" ? "Deployment" : "Transfer execution"}</b> #{d.execution_number} of{" "}
            {(d.instances || []).join(", ")} · {d.source?.name} ({d.source?.type}) → {d.destination?.name} (
            {d.destination?.type})
          </div>
          <div>
            Started {when(d.started_at?.replace("+00:00", ""))} · finished {when(d.finished_at?.replace("+00:00", ""))} ·{" "}
            {duration(d.duration_seconds)} · by {r.user_name || r.user_id}
          </div>
          {(d.summary || []).length ? <MonoBlock>{d.summary.join("\n")}</MonoBlock> : null}
          {(d.errors || []).map((e: any, i: number) => (
            <MonoBlock key={i} style={{ color: ThemePalette.alert }}>
              {e.task}: {e.error}
            </MonoBlock>
          ))}
          <Secondary>Transfer {r.resource_id} · execution {d.execution_id}</Secondary>
        </Details>
      );
    }
    if (r.kind === "operation") {
      const check = d.check || d.test_data;
      return (
        <Details>
          <div>
            <b>{opLabel(r.action)}</b> · {r.resource_type} {r.resource_id} · result <b>{r.status}</b>
            {d.backfilled ? " · backfilled from the platform history" : ""}
          </div>
          {d.performed_by ? <div>Done by: {d.performed_by}</div> : null}
          {d.reason ? <div>Reason: {d.reason}</div> : null}
          {d.evidence ? <Secondary>Evidence: {d.evidence}</Secondary> : null}
          {d.log ? <MonoBlock>{d.log}</MonoBlock> : null}
          {check ? <MonoBlock>Check: {JSON.stringify(check, null, 2)}</MonoBlock> : null}
          {d.state_before_delete ? (
            <MonoBlock>State before delete: {JSON.stringify(d.state_before_delete, null, 2)}</MonoBlock>
          ) : null}
          {d.error ? <MonoBlock style={{ color: ThemePalette.alert }}>{d.error}</MonoBlock> : null}
        </Details>
      );
    }
    return (
      <Details>
        <div>
          {r.method} {r.path} → HTTP {r.status_code} · request {r.request_id}
        </div>
        {d.body ? <MonoBlock>{JSON.stringify(d.body, null, 2)}</MonoBlock> : null}
      </Details>
    );
  }

  renderToolbar(records: AuditRecord[]) {
    const q = this.props.query;
    const all = this.state.records;
    const forResult = this.filtered("result");
    const count = (g: string) => forResult.filter(r => RESULTS[g].includes(r.status || "")).length;
    const distinct = (values: (string | null | undefined)[]) =>
      [...new Set(values.filter(Boolean) as string[])].sort().map(v => ({ value: v, label: v }));
    return (
      <ListToolbar>
        <ToolbarSearch value={q.get("q")} placeholder="Search VM, user, ID or path" onChange={v => q.set({ q: v })} />
        <Segmented
          label="Result"
          value={q.get("result")}
          onChange={v => q.set({ result: v })}
          options={[
            { value: "", label: "All" },
            { value: "succeeded", label: "Succeeded", count: count("succeeded") },
            { value: "failed", label: "Failed", count: count("failed") },
            { value: "cancelled", label: "Cancelled", count: count("cancelled") },
          ]}
        />
        <FilterSelect label="Records" value={q.get("kind")} onChange={v => q.set({ kind: v })} options={KINDS} />
        <FilterSelect
          label="Actor"
          value={q.get("actor")}
          onChange={v => q.set({ actor: v })}
          options={distinct(all.map(actorOf))}
          allLabel="Anyone"
        />
        <FilterSelect
          label="Action"
          value={q.get("action")}
          onChange={v => q.set({ action: v })}
          options={distinct(all.map(r => r.action || r.method))}
          allLabel="Any"
        />
        <FilterSelect
          label="Resource"
          value={q.get("rtype")}
          onChange={v => q.set({ rtype: v })}
          options={distinct(all.map(r => r.resource_type))}
          allLabel="Any"
        />
        <ToolbarDateRange
          label="Date"
          from={q.get("from")}
          to={q.get("to")}
          onChange={r => q.set({ from: r.from, to: r.to })}
        />
        {this.filtersActive ? <ClearFiltersButton onClick={() => this.clearFilters()} /> : null}
        <ToolbarSpacer />
        <ToolbarCount>{pluralise(records.length, "record")}</ToolbarCount>
        <ExportButton type="button" onClick={() => this.exportCsv(records)} disabled={!records.length}>
          <Download size={14} />
          Export CSV
        </ExportButton>
        <RefreshButton onClick={() => this.load()} />
      </ListToolbar>
    );
  }

  renderRows(records: AuditRecord[]) {
    const { expanded } = this.state;
    if (this.state.loading && !this.state.records.length) {
      return (
        <Loading>
          <LoaderCircle className="spin" size={20} />
          Loading the audit history…
        </Loading>
      );
    }
    if (!records.length) {
      return this.filtersActive ? (
        <EmptyState
          compact
          icon={SearchX}
          tone="neutral"
          title="No records match these filters."
          message="Try a different search or date range, or clear the filters."
          secondaryLabel="Clear filters"
          onSecondary={() => this.clearFilters()}
        />
      ) : (
        <EmptyState
          compact
          icon={History}
          title="No audit records yet"
          message="Every change made through SwiftIQ and every migration run is recorded here."
        />
      );
    }
    return (
      <>
        <ListHeader columns={AUDIT_COLUMNS} />
        {records.map(r => {
          const { what, extra } = describe(r);
          const open = expanded === r.id;
          return (
            <React.Fragment key={r.id}>
              <ListRow
                columns={AUDIT_COLUMNS}
                onClick={() => this.setState({ expanded: open ? null : r.id })}
                trailing={<Chevron size={16} open={open} />}
                testId="auditRow"
              >
                <Cell title={when(r.created_at)}>{when(r.created_at)}</Cell>
                <Cell title={actorOf(r)}>{actorOf(r) || <Muted>—</Muted>}</Cell>
                <Stack>
                  <Primary title={what} style={{ fontSize: 13 }}>{what}</Primary>
                  <Secondary>{r.kind === "execution" ? "Migration run" : r.kind === "operation" ? "Operation" : "Change"}</Secondary>
                </Stack>
                <Cell title={r.resource_type || ""}>{r.resource_type || <Muted>—</Muted>}</Cell>
                <Cell title={extra}>{extra || <Muted>—</Muted>}</Cell>
                <div>
                  <ResultPill status={r.status} />
                </div>
              </ListRow>
              {open ? this.renderDetails(r) : null}
            </React.Fragment>
          );
        })}
      </>
    );
  }

  renderList() {
    const records = this.filtered();
    return (
      <Card>
        {this.renderToolbar(records)}
        <Scroll>
          <Inner>{this.renderRows(records)}</Inner>
        </Scroll>
        <Footnote>
          Records are append-only: they cannot be changed or deleted, and they stay after the transfers,
          deployments or endpoints they refer to are deleted.
        </Footnote>
      </Card>
    );
  }

  render() {
    return (
      <Wrapper>
        <MainTemplate
          navigationComponent={<Navigation currentPage="audit" />}
          listNoMargin
          listComponent={this.renderList()}
          headerComponent={<PageHeader title="Audit history" subtitle="Who did what in SwiftIQ, and when." onProjectChange={() => this.load()} />}
        />
      </Wrapper>
    );
  }
}

function AuditPage() {
  const query = useListQuery();
  return <AuditPageContent query={query} />;
}

export default AuditPage;
